import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { UploadCloud, FileText, Trash2, Download } from "lucide-react";

export default function CaseDocuments({ caseId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inp = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/cases/${caseId}/documents`);
    setDocs(data);
  };
  useEffect(() => { load(); }, [caseId]);

  const upload = async (f) => {
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/cases/${caseId}/documents`, fd, { headers: {"Content-Type":"multipart/form-data"} });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail?.toString() || e.message);
    } finally { setUploading(false); if (inp.current) inp.current.value = ""; }
  };

  const del = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`); load();
  };

  const download = async (d) => {
    const token = localStorage.getItem("icos_token");
    const resp = await fetch(`${API}/documents/${d.id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = d.original_filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="mt-8 border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Documents</div>
        <label data-testid="upload-doc-label" className={`border border-[#0033A0] text-[#0033A0] px-4 py-2 text-xs flex items-center gap-2 cursor-pointer hover:bg-[#0033A0] hover:text-white ${uploading ? "opacity-50" : ""}`}>
          <UploadCloud size={14} /> {uploading ? "Uploading…" : "Upload"}
          <input ref={inp} data-testid="upload-doc-input" type="file" className="hidden"
            onChange={e => e.target.files?.[0] && upload(e.target.files[0])} disabled={uploading} />
        </label>
      </div>
      {err && <div className="border border-red-300 bg-red-50 text-red-700 text-xs px-3 py-2 mb-3">{err}</div>}
      {docs.length === 0 && <div className="text-sm text-slate-500">No documents attached yet.</div>}
      <ul className="divide-y divide-slate-100">
        {docs.map(d => (
          <li key={d.id} className="py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{d.original_filename}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {(d.size/1024).toFixed(1)} KB · {d.uploaded_by} · {d.uploaded_at?.slice(0,10)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`download-doc-${d.id}`} onClick={() => download(d)} className="p-2 border border-slate-200 hover:bg-slate-900 hover:text-white"><Download size={14} /></button>
              <button onClick={() => del(d.id)} className="p-2 border border-slate-200 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
